import React, {
  forwardRef,
  KeyboardEvent,
  ReactNode,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  NativeSyntheticEvent,
  Platform,
  TextInput,
  TextInputContentSizeChangeEventData,
  TextInputKeyPressEventData,
  TouchableOpacity,
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { isEmpty, noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { hasTokenPattern } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/tokens'
import { convertHtmlSpace } from '@lyrahealth-inc/shared-app-logic/src/features/selfCareMedia/utils'

const hasHtml = (text: string): boolean => {
  if (!text) return false
  const htmlTagPattern = /<[^>]+>/
  return htmlTagPattern.test(text)
}

const hasErrorSpanPattern = (text: string): boolean => {
  const spanErrorPattern = /<span[^>]*class="error-text"[^>]*>\[ERROR:[^\]]+\]<\/span>/
  return spanErrorPattern.test(text)
}

import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { BaseInputField } from '../../atoms/baseInputField/BaseInputField'
import { SendArrowIcon } from '../../atoms/icons/SendArrowIcon'
import { SendIcon } from '../../atoms/icons/SendIcon'
import { WaveFormIcon } from '../../atoms/icons/WaveFormIcon'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TokenTextInput } from '../../atoms/tokenTextInput/TokenTextInput.web'
import { IS_WEB, SUPPORTS_FIELD_SIZING } from '../../constants'
import { AppContext } from '../../context'
import { OSs, useOS } from '../../hooks/useOS'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ChatInputProps = {
  onSendMessage: (text: string) => void
  onInputChange: (text: string) => void
  onInputHeightChange?: (height: number) => void
  previousInputValue?: string
  actions?: ReactNode[]
  useActionsLayout?: boolean
  selection?: { start: number; end: number }
  sendOnEnter?: boolean
  attachments?: ReactNode[]
  topAttachments?: ReactNode[]
  expandedButton?: boolean
  disableSend?: boolean
  onVoiceChatPressed?: () => void
  isAiCoachSelected?: boolean
  hasAttachmentToSend?: boolean
}

const SendMessage = styled(Flex1View)(({ theme }) => ({
  justifyContent: Platform.OS === 'web' ? 'flex-end' : 'center',
  alignItems: 'center',
  flexGrow: 0,
  marginLeft: theme.spacing['12px'],
  alignSelf: 'flex-end',
}))

const OuterContainer = styled(SafeAreaView)<{ hasActions: boolean }>(({ theme, hasActions }) => ({
  padding: hasActions ? theme.spacing['16px'] : theme.spacing['0px'],
  backgroundColor: theme.breakpoints.isMobileSized ? theme.colors.backgroundPrimary : undefined,
}))

const Container = styled(SafeAreaView)<{ hasActions: boolean; focused: boolean }>(({ theme, hasActions, focused }) => ({
  flexDirection: 'row',
  padding: theme.spacing['16px'],
  paddingLeft: theme.spacing['12px'],
  alignItems: 'center',
  ...(hasActions
    ? {
        borderColor: focused ? theme.colors.borderFocus : theme.colors.borderDefault,
        borderWidth: '1px',
        borderBottomWidth: 0,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
      }
    : {}),
  background: theme.breakpoints.isMobileSized ? theme.colors.backgroundPrimary : undefined,
}))

const InputScrollView = styled.ScrollView<{ hasActions: boolean }>(({ hasActions }) => ({
  maxHeight: '212px',
  borderColor: 'transparent',
  borderWidth: hasActions ? 0 : '1px',
  borderRadius: '4px',
}))

const ActionsContainer = styled.View<{ expandedButton: boolean; focused: boolean }>(
  ({ theme, expandedButton, focused }) => ({
    padding: theme.spacing['8px'],
    paddingRight: expandedButton ? theme.spacing['8px'] : theme.spacing['16px'],
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: '1px',
    borderTopWidth: '0px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    borderColor: focused ? theme.colors.borderFocus : theme.colors.borderDefault,
    backgroundColor: theme.breakpoints.isMobileSized ? theme.colors.backgroundPrimary : undefined,
  }),
)

const ActionsInnerContainer = styled.View({
  flexDirection: 'row',
})

const AttachmentsContainer = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['12px'],
}))

const SendButton = styled(TouchableOpacity)({})

const INPUT_SCROLL_CONTENT_STYLE = { justifyContent: 'flex-start' } as const

const CHAT_INPUT_LINE_HEIGHT = 24
const CHAT_INPUT_MAX_LINES = 4

export type ChatInputHandle = {
  focusInput: () => void
  insertMessage: (updateMessage: string) => void
  get isReady(): boolean
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      onSendMessage,
      onInputChange,
      onInputHeightChange,
      previousInputValue,
      selection = undefined,
      sendOnEnter = false,
      actions = [],
      useActionsLayout = false,
      attachments = [],
      topAttachments = [],
      expandedButton = false,
      disableSend = false,
      onVoiceChatPressed = noop,
      isAiCoachSelected = false,
      hasAttachmentToSend = false,
    },
    ref,
  ) => {
    const { colors, isAuroraTheme } = useTheme() as ThemeType
    const { chatInput } = colors.components
    const { formatMessage } = useIntl()
    const { isAiCoachVoiceEnabled } = useContext(AppContext)
    const [keyMap, setKeyMap] = useState<{ [key: string]: number }>({})
    const [shiftKeyPressed, setShiftKeyPressed] = useState(false)
    const inputMinHeight = CHAT_INPUT_LINE_HEIGHT + (Platform.OS === 'ios' ? 8 : 0)
    const inputMaxHeight = inputMinHeight + (CHAT_INPUT_MAX_LINES - 1) * CHAT_INPUT_LINE_HEIGHT
    const [inputHeight, setInputHeight] = useState(inputMinHeight)
    const [scrollEnabled, setScrollEnabled] = useState(false)
    const [inputValue, setInputValue] = useState(previousInputValue)
    const isValidInput = !isEmpty(inputValue?.trim())
    const [focused, setFocused] = useState(false)
    const hasRichTextTokensOrHtml =
      hasTokenPattern(inputValue ?? '') || hasErrorSpanPattern(inputValue ?? '') || hasHtml(inputValue ?? '')
    const shouldUseRichTextInput = hasRichTextTokensOrHtml && inputValue && !isEmpty(inputValue)
    const inputRef = useRef<TextInput>(null)
    const readyRef = useRef(false)
    const canSend = (isValidInput || hasAttachmentToSend) && !disableSend
    const isSendDisabled = !canSend

    useImperativeHandle(
      ref,
      () => ({
        focusInput: () => {
          inputRef.current?.focus()
        },
        insertMessage: (message) => {
          setInputValue(message)
        },
        get isReady() {
          return readyRef.current
        },
      }),
      [],
    )

    useEffect(() => {
      setInputValue(previousInputValue)
    }, [previousInputValue])
    const inputChanged = (text: string) => {
      setInputValue(text)
      onInputChange(text)
    }

    const send = () => {
      if (!inputValue || disableSend) {
        return
      }
      // on android the height is not reset, so make sure it is reset here after sending
      setInputHeight(inputMinHeight)
      setScrollEnabled(false)
      setInputValue('')
      setKeyMap({})
      // Call send message asynchronously so the text field will reset immediately
      setTimeout(() => {
        inputValue && onSendMessage(inputValue)
      })

      if (shouldUseRichTextInput) {
        inputChanged('')
      }
    }

    // When a user inputs text for multiline, the input should increase height for each line
    const onContentSizeChange = (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      const newHeight = event.nativeEvent.contentSize.height
      if (newHeight !== inputHeight) {
        setInputHeight(newHeight)
        onInputHeightChange && onInputHeightChange(Math.max(inputMinHeight, newHeight))
      }
      // iOS only trick to enable scrolling on resizable text field.
      setScrollEnabled(newHeight >= inputMaxHeight)
    }

    // taken from ui-core
    const KEYS = {
      ENTER: 'Enter',
      CONTROL: 'Control',
      META: 'Meta', // CMD key for mac,
      SHIFT: 'Shift',
    }
    /**
     * This logic is borrowed from ui-core.
     * Set up a key map and check if user has pressed ctrl or command (meta) + Enter to send
     */
    const handleKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (e.defaultPrevented || Platform.OS !== 'web' || disableSend) {
        return
      }
      const key = e.nativeEvent.key
      const timeStamp = e.timeStamp
      if (key) {
        if (key === KEYS.SHIFT) {
          setShiftKeyPressed(true)
        } else if ([KEYS.META, KEYS.CONTROL].indexOf(key) > -1) {
          // cache the key to check for a simutanous key on next event
          setKeyMap({ ...keyMap, [key]: timeStamp })
        } else if ([KEYS.ENTER].indexOf(key) > -1) {
          const sendWithCommandKey = timeStamp - keyMap[KEYS.META]
          const sendWithControlKey = timeStamp - keyMap[KEYS.CONTROL]

          // working with on keypress instead of (keyUp and keyDown), so we detect time between key presses
          const shouldSend = (sendOnEnter && !shiftKeyPressed) || sendWithCommandKey < 200 || sendWithControlKey < 200

          if (shouldSend && isValidInput) {
            e.preventDefault()
            send()
          } else {
            setKeyMap({})
          }
        } else {
          setKeyMap({})
        }
      }
    }

    // handleKeyUp is only supported on web
    const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
      const key = e.nativeEvent.key
      if (key === KEYS.SHIFT) {
        setShiftKeyPressed(false)
      }
    }

    const os = useOS()
    const tooltipContent = useMemo(() => {
      if (sendOnEnter) {
        return formatMessage({ defaultMessage: 'Send (↵ Enter)', description: 'Send message with enter key' })
      }

      if (os === OSs.MAC) {
        return formatMessage({
          defaultMessage: 'Send (⌘ + Enter)',
          description: 'Send message with enter and meta key on Mac',
        })
      }
      return formatMessage({
        defaultMessage: 'Send (Ctrl + Enter)',
        description: 'Send message with enter and meta key on windows',
      })
    }, [formatMessage, os, sendOnEnter])

    const sendMessageButton = expandedButton ? (
      <PrimaryButton
        testID={tID('Chat-sendButton')}
        disabled={isSendDisabled}
        text={<FormattedMessage defaultMessage='Send' description='Send message button label' />}
        onPress={send}
        leftIcon={<SendIcon fillColor={colors.primaryButtonTextActive} isFilled />}
        accessibilityLabel={tooltipContent}
      />
    ) : (
      <SendButton
        onPress={send}
        disabled={isSendDisabled}
        testID={tID('Chat-sendButton')}
        accessibilityLabel={tooltipContent}
        accessibilityRole='button'
        style={{ backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, 'transparent') }}
      >
        <SendArrowIcon
          fillColor={
            canSend
              ? (chatInput.sendButtonIconActive ?? colors.iconInverse)
              : (chatInput.sendButtonIconInactive ?? colors.iconInactive)
          }
          backgroundColor={getAuroraAwareBackgroundColor(
            isAuroraTheme,
            canSend ? colors.backgroundActive : colors.backgroundInactive,
            canSend
              ? (chatInput.sendButtonBackgroundActive ?? 'transparent')
              : (chatInput.sendButtonBackgroundInactive ?? 'transparent'),
          )}
          testID={tID('Chat-sendIcon')}
        />
      </SendButton>
    )
    const aiVoiceChatButton = (
      <PrimaryButton
        testID={tID('Chat-aiVoiceChatButton')}
        text={<FormattedMessage defaultMessage='Voice' description='AI voice chat button label' />}
        onPress={onVoiceChatPressed}
        leftIcon={<WaveFormIcon fillColor={colors.primaryButtonTextActive} />}
        size={ButtonSize.EXTRA_SMALL}
      />
    )
    useEffect(() => {
      readyRef.current = true
    }, [])
    const hasActions = actions.length > 0
    const showActionsLayout = useActionsLayout || hasActions
    return (
      <OuterContainer edges={['left', 'right']} hasActions={showActionsLayout}>
        {topAttachments.length > 0 && topAttachments}
        <Container
          edges={['left', 'right']}
          testID={tID('Chat-inputField')}
          hasActions={showActionsLayout}
          focused={focused}
        >
          <InputScrollView hasActions={showActionsLayout} contentContainerStyle={INPUT_SCROLL_CONTENT_STYLE}>
            {shouldUseRichTextInput ? (
              <TokenTextInput initialValue={inputValue} onChange={inputChanged} />
            ) : (
              <BaseInputField
                ref={inputRef}
                borderColor={chatInput.border}
                onContentSizeChange={onContentSizeChange}
                onInputChange={inputChanged}
                inputValue={inputValue ? convertHtmlSpace(inputValue) : ''}
                placeholderText={formatMessage({
                  defaultMessage: 'Write a message...',
                  description: 'Placeholder text for chat input field',
                })}
                minHeight={
                  IS_WEB && !SUPPORTS_FIELD_SIZING
                    ? Math.min(Math.max(inputMinHeight, inputHeight), inputMaxHeight)
                    : inputMinHeight
                }
                maxHeight={inputMaxHeight}
                scrollEnabled={scrollEnabled}
                multiline={true}
                width={'100%'}
                handleKeyPress={Platform.OS === 'web' ? handleKeyPress : undefined}
                handleKeyUp={handleKeyUp}
                outlineColor={chatInput.border}
                outlineStyle={'none'}
                onBlur={() => setFocused(false)}
                onFocus={() => setFocused(true)}
                selection={selection}
              />
            )}
            {!isEmpty(attachments) && <AttachmentsContainer>{attachments}</AttachmentsContainer>}
          </InputScrollView>
          {!showActionsLayout && (
            <SendMessage>
              {isAiCoachVoiceEnabled && isAiCoachSelected && !isValidInput ? aiVoiceChatButton : sendMessageButton}
            </SendMessage>
          )}
        </Container>
        {showActionsLayout && (
          <ActionsContainer expandedButton={expandedButton} focused={focused}>
            <ActionsInnerContainer>{actions}</ActionsInnerContainer>
            {sendMessageButton}
          </ActionsContainer>
        )}
      </OuterContainer>
    )
  },
)
